import { useState, useCallback, useRef, useEffect } from 'react';
import styles from './NicknameForm.module.css';

/**
 * Nickname validation rules:
 * - 5-15 characters (inclusive)
 * - Only alphanumeric characters (a-z, A-Z, 0-9), hyphens, and underscores
 * - No spaces
 */
const NICKNAME_PATTERN = /^[a-zA-Z0-9_-]{5,15}$/;
const MIN_LENGTH = 5;
const MAX_LENGTH = 15;

export interface NicknameFormProps {
  onComplete: (nickname: string) => void;
  initialValue?: string;
}

type ValidationState = 'idle' | 'valid' | 'invalid';

interface ClientValidationResult {
  isValid: boolean;
  error: string | null;
}

function validateNicknameClient(value: string): ClientValidationResult {
  if (value.length === 0) {
    return { isValid: false, error: null };
  }

  if (value.includes(' ')) {
    return { isValid: false, error: 'Nickname must not contain spaces' };
  }

  if (value.length < MIN_LENGTH) {
    return { isValid: false, error: 'Too short' };
  }

  if (value.length > MAX_LENGTH) {
    return { isValid: false, error: 'Too long' };
  }

  if (!NICKNAME_PATTERN.test(value)) {
    return { isValid: false, error: 'Invalid characters' };
  }

  return { isValid: true, error: null };
}

export function NicknameForm({ onComplete, initialValue = '' }: NicknameFormProps) {
  const [value, setValue] = useState(initialValue);
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const [clientError, setClientError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const validateDebounced = useCallback((input: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const result = validateNicknameClient(input);
      if (input.length === 0) {
        setValidationState('idle');
        setClientError(null);
      } else if (result.isValid) {
        setValidationState('valid');
        setClientError(null);
      } else {
        setValidationState('invalid');
        setClientError(result.error);
      }
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setServerError(null);
    validateDebounced(newValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Run immediate client validation before submitting
    const result = validateNicknameClient(value);
    if (!result.isValid) {
      setValidationState('invalid');
      setClientError(result.error);
      return;
    }

    setIsSubmitting(true);
    setServerError(null);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: value }),
      });

      if (response.ok) {
        onComplete(value);
      } else {
        const data = await response.json();
        const errorMessage = mapServerError(data.error || data.message || 'Validation failed');
        setServerError(errorMessage);
        setValidationState('invalid');
      }
    } catch {
      setServerError('Unable to verify nickname. Please try again.');
      setValidationState('invalid');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = serverError || clientError;
  const inputClassName = [
    styles.input,
    validationState === 'valid' ? styles.inputValid : '',
    validationState === 'invalid' ? styles.inputInvalid : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <form className={styles.container} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.title}>Choose Your Nickname</h2>

      <div className={styles.inputWrapper}>
        <input
          type="text"
          id="nickname-input"
          className={inputClassName}
          value={value}
          onChange={handleChange}
          maxLength={MAX_LENGTH}
          autoComplete="off"
          aria-label="Nickname"
          aria-describedby="nickname-hint nickname-error"
          aria-invalid={validationState === 'invalid'}
          disabled={isSubmitting}
          placeholder="Enter nickname"
        />
        {validationState === 'valid' && (
          <span className={`${styles.indicator} ${styles.indicatorValid}`} aria-hidden="true">
            ✓
          </span>
        )}
        {validationState === 'invalid' && (
          <span className={`${styles.indicator} ${styles.indicatorInvalid}`} aria-hidden="true">
            ✗
          </span>
        )}
      </div>

      <p id="nickname-hint" className={styles.hint}>
        5-15 characters: letters, numbers, hyphens, underscores. No spaces.
      </p>

      <p id="nickname-error" className={styles.error} role="alert" aria-live="polite">
        {displayError || ''}
      </p>

      <button
        type="submit"
        className={styles.submitButton}
        disabled={isSubmitting || validationState !== 'valid'}
      >
        {isSubmitting ? 'Checking...' : 'Confirm Nickname'}
      </button>
    </form>
  );
}

function mapServerError(message: string): string {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('taken') || lowerMessage.includes('already')) {
    return 'Already taken';
  }
  if (lowerMessage.includes('offensive') || lowerMessage.includes('profan')) {
    return 'Offensive language';
  }
  if (lowerMessage.includes('short')) {
    return 'Too short';
  }
  if (lowerMessage.includes('long')) {
    return 'Too long';
  }
  if (lowerMessage.includes('character') || lowerMessage.includes('invalid') || lowerMessage.includes('format')) {
    return 'Invalid characters';
  }

  return message;
}

export default NicknameForm;
